// §11.1 — the filesystem operations behind create, paste and move.
//
// Every one of them goes through `HangarAPI.fs`, which is the §9.5-scoped gateway: Explorer has
// no other route to the disk, and a path outside what §9.5 grants it fails here exactly as it
// would for any other app. Kept out of the component so the collision rules can be tested.
import {
  basename,
  joinVfs,
  parentOf,
  type FileEntry,
  type HangarFsApi,
  type SendFileOutcome,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import type { FileClip } from './clipboard';
import { uniqueName } from './model';

/** The names already in use in `dir`, so a new one can avoid them. */
export async function namesIn(fs: HangarFsApi, dir: VfsPath): Promise<string[]> {
  return (await fs.list(dir)).map((entry) => entry.name);
}

/** A free path for `name` inside `dir` — never an overwrite (D-045). */
export async function freeIn(fs: HangarFsApi, dir: VfsPath, name: string): Promise<VfsPath> {
  return joinVfs(dir, uniqueName(await namesIn(fs, dir), name));
}

export async function createFolder(fs: HangarFsApi, dir: VfsPath, name: string): Promise<VfsPath> {
  const target = await freeIn(fs, dir, name);
  await fs.mkdir(target);
  return target;
}

export async function createFile(
  fs: HangarFsApi,
  dir: VfsPath,
  name: string,
  contents = '',
): Promise<VfsPath> {
  const target = await freeIn(fs, dir, name);
  await fs.write(target, contents);
  return target;
}

/**
 * `HangarFsApi` has no `stat` (§4.1), so an entry is found by listing its parent. Explorer is
 * the file manager and reaches every parent it can show; this is the one lookup a path-only
 * clipboard costs.
 */
async function entryAt(fs: HangarFsApi, path: VfsPath): Promise<FileEntry | undefined> {
  return (await fs.list(parentOf(path))).find((entry) => entry.path === path);
}

/** Copy one entry into `dir` under `name`, recursing through directories. */
export async function copyEntry(
  fs: HangarFsApi,
  source: FileEntry,
  dir: VfsPath,
  name: string,
): Promise<VfsPath> {
  const target = joinVfs(dir, name);
  if (!source.isDir) {
    await fs.write(target, await fs.read(source.path));
    return target;
  }
  await fs.mkdir(target);
  // Children come from the listing that already knows what each one is — no second lookup.
  for (const child of await fs.list(source.path)) {
    await copyEntry(fs, child, target, child.name);
  }
  return target;
}

/**
 * Complete a pending cut or copy into `dir`. Returns what landed, so the caller can select it.
 * A cut of something already in `dir` is a no-op rather than a rename to "(2)".
 */
export async function paste(
  fs: HangarFsApi,
  clip: FileClip,
  dir: VfsPath,
): Promise<{ landed: VfsPath[]; failed: number }> {
  const taken = new Set(await namesIn(fs, dir));
  const landed: VfsPath[] = [];
  let failed = 0;

  for (const source of clip.paths) {
    if (clip.mode === 'cut' && parentOf(source) === dir) {
      landed.push(source);
      continue;
    }
    const name = uniqueName(taken, basename(source));
    taken.add(name);
    try {
      if (clip.mode === 'cut') {
        landed.push((await fs.move(source, joinVfs(dir, name))).path);
      } else {
        const entry = await entryAt(fs, source);
        if (!entry) throw new Error(`${source} is gone`);
        landed.push(await copyEntry(fs, entry, dir, name));
      }
    } catch {
      // One unreadable item must not abandon the rest of the paste.
      failed += 1;
      taken.delete(name);
    }
  }

  return { landed, failed };
}

/** Move a selection into `dir` — what a drag onto a folder does. */
export async function moveInto(
  fs: HangarFsApi,
  sources: readonly VfsPath[],
  dir: VfsPath,
): Promise<{ landed: VfsPath[]; failed: number }> {
  return paste(fs, { mode: 'cut', paths: sources }, dir);
}

/** Rename in place. §7.1 has no rename, so it is a move within the same parent. */
export async function renameTo(fs: HangarFsApi, path: VfsPath, name: string): Promise<VfsPath> {
  const dir = parentOf(path);
  const trimmed = name.trim();
  if (trimmed.length === 0 || trimmed === basename(path)) return path;
  const taken = (await namesIn(fs, dir)).filter((existing) => existing !== basename(path));
  return (await fs.move(path, joinVfs(dir, uniqueName(taken, trimmed)))).path;
}

// ── §34.2 (M56.5) — Compress to zip.
//
// Explorer owns this rather than Archive because it is a *selection* verb: the thing being acted
// on is what is highlighted in this pane. The format itself comes from `assets/archive/format`,
// which §6.5 permits (an app may not import another app, but both may share `assets/**` — D-170),
// and it is imported **dynamically** so a desktop that never compresses anything never fetches a
// deflater (D-507).

/** Whether the menu offers it: two or more things selected (M56.5). */
export function canCompress(selection: readonly VfsPath[]): boolean {
  return selection.length >= 2;
}

/**
 * What the archive is called — the containing folder's own name, beside the selection.
 *
 * At the filesystem root there is no folder name to take, so it falls back rather than producing
 * `.zip` with an empty stem.
 */
export function compressName(dir: VfsPath): string {
  // `basename('/')` answers `/`, not the empty string — so the guard tests for a real name
  // rather than for falsiness, which is what produced `/.zip` the first time.
  const name = basename(dir);
  return `${name && name !== '/' ? name : 'Archive'}.zip`;
}

/**
 * Read the selection into archive members, walking directories.
 *
 * Paths inside the archive are **relative to `dir`**, so unzipping the result beside the original
 * reproduces exactly what was selected rather than a chain of parent folders.
 */
export async function gatherForZip(
  fs: HangarFsApi,
  dir: VfsPath,
  selection: readonly VfsPath[],
): Promise<Array<{ path: string; data: Uint8Array }>> {
  const prefix = dir === '/' ? 1 : dir.length + 1;
  const members: Array<{ path: string; data: Uint8Array }> = [];

  async function take(path: VfsPath, isDir: boolean): Promise<void> {
    if (!isDir) {
      members.push({ path: path.slice(prefix), data: await fs.read(path) });
      return;
    }
    for (const child of await fs.list(path)) await take(child.path, child.isDir);
  }

  const listing = await fs.list(dir);
  for (const path of selection) {
    const entry = listing.find((candidate) => candidate.path === path);
    await take(path, entry?.isDir ?? false);
  }
  return members;
}

/** Compress the selection into `<parent>.zip` beside it, and answer where it landed. */
export async function compressSelection(
  fs: HangarFsApi,
  dir: VfsPath,
  selection: readonly VfsPath[],
): Promise<VfsPath> {
  const members = await gatherForZip(fs, dir, selection);
  const { create } = await import('@/assets/archive/format');
  const bytes = await create(members);
  const target = await freeIn(fs, dir, compressName(dir));
  await fs.write(target, bytes);
  return target;
}

// ── §34.13 (M66.3) — Send to connection.
//
// Explorer owns the verb for M56.5's reason — it is a *selection* verb, and §6.5 forbids importing
// another app — but it owns **only the verb**. The roster, the picker and the request are the
// shell's (D-583), reached through one capability call; what is left here is turning the outcome
// into the one sentence PLAN §7.5 binds for it. A `switch` with no `default`, so a case added to
// `SendFileOutcome` is a type error rather than a status line that silently says nothing.

/** The status-line sentence for a send outcome. Every literal is §34.13/PLAN §7.5's, verbatim. */
export function sendStatus(outcome: SendFileOutcome): string {
  switch (outcome.kind) {
    case 'sent':
      return COPY.offers.sent(outcome.name, outcome.toName);
    case 'cancelled':
      return COPY.offers.sendCancelled;
    case 'device-local':
      return COPY.offers.deviceLocal;
    case 'not-connected':
      return COPY.offers.notConnected;
    case 'failed':
      return COPY.offers.sendFailed;
  }
}
